// API Client - Connects to FastAPI backend
// Re-exports types from types/index.ts for convenience

import type { Account, BankAccount, DashboardSummary, JournalEntry, TaxCode, TrialBalance } from '../types/index';

import { supabase } from '@/lib/supabase';

export type {
  Account,
  AccountType,
  TaxCode,
  TaxCodeType,
  BankAccount,
  JournalEntry,
  JournalEntryLine,
  TrialBalance,
  DashboardSummary,
} from '../types/index';

// API base URL
const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

// Current organization ID (set by OrganizationProvider)
let currentOrganizationId: number | null = null;

export function setOrganizationId(orgId: number | null) {
  currentOrganizationId = orgId;
}

export function getOrganizationId(): number | null {
  return currentOrganizationId;
}

// Generic fetch wrapper with auth
async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge existing headers if provided
  if (options?.headers) {
    const existingHeaders = options.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  // Add Authorization header if token exists
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Add X-Organization-ID header if organization is set
  if (currentOrganizationId) {
    headers['X-Organization-ID'] = String(currentOrganizationId);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401) {
    // Clear session and redirect to login
    await supabase.auth.signOut();
    window.location.href = '/login';
    throw new Error('Unauthorized - redirecting to login');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' })) as { message?: string; detail?: string };
    throw new Error(error.message ?? error.detail ?? `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}

// Inventory Valuation Item type
interface InventoryValuationItem {
  sku: string;
  name: string;
  quantity: number;
  unit_cost: number;
  total_value: number;
  category?: string;
}

// Accounts API
export const accountsApi = {
  async list(filters?: { type?: string; is_active?: boolean }): Promise<Account[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.set('account_type', filters.type);
    const query = params.toString();
    return apiFetch<Account[]>(`/accounts${query ? `?${query}` : ''}`);
  },

  async get(id: number): Promise<Account> {
    return apiFetch<Account>(`/accounts/${id}`);
  },

  async create(data: Partial<Account>): Promise<Account> {
    return apiFetch<Account>('/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async update(id: number, data: Partial<Account>): Promise<Account> {
    return apiFetch<Account>(`/entities/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async delete(id: number): Promise<void> {
    await apiFetch<void>(`/entities/accounts/${id}`, { method: 'DELETE' });
  },
};

// Tax Codes API
export const taxCodesApi = {
  async list(): Promise<TaxCode[]> {
    return apiFetch<TaxCode[]>('/entities/tax-codes');
  },
};

// Bank Accounts API
export const bankAccountsApi = {
  async list(): Promise<BankAccount[]> {
    return apiFetch<BankAccount[]>('/entities/bank-accounts');
  },
};

// Journal Entries API
export const journalEntriesApi = {
  async list(): Promise<JournalEntry[]> {
    return apiFetch<JournalEntry[]>('/journal');
  },
};

// Dashboard API
export const dashboardApi = {
  async getSummary(): Promise<DashboardSummary> {
    const [tb, accounts] = await Promise.all([
      reportsApi.getTrialBalance(),
      accountsApi.list(),
    ]);
    
    return {
      bank_accounts_count: 3,
      total_accounts: accounts.length,
      tax_codes_count: 9,
      unreconciled_count: 0,
      trial_balance: tb,
      accounts_by_type: accounts.reduce((acc, a) => {
        acc[a.type] = (acc[a.type] ?? 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    };
  },
};

// Reports API
export const reportsApi = {
  async getTrialBalance(): Promise<TrialBalance> {
    return apiFetch<TrialBalance>('/reports/trial-balance');
  },

  async getBalanceSheet(asOf: string): Promise<unknown> {
    return apiFetch(`/reports/balance-sheet?as_of=${asOf}`);
  },

  async getProfitLoss(startDate: string, endDate: string): Promise<unknown> {
    return apiFetch(`/reports/income-statement?start_date=${startDate}&end_date=${endDate}`);
  },

  async getInventoryValuation(): Promise<{ items: InventoryValuationItem[]; total_value: number; as_of: string; currency: string }> {
    return apiFetch('/reports/inventory-valuation');
  },
};

// Export all
export const api = {
  accounts: accountsApi,
  taxCodes: taxCodesApi,
  bankAccounts: bankAccountsApi,
  journalEntries: journalEntriesApi,
  dashboard: dashboardApi,
  reports: reportsApi,
};

export default api;